import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';
import useRequirePermission from '../../hooks/useRequirePermission';
import ValidatePinModal from '../modals/ValidatePinModal';
import { presetsByGroup, findPreset, extractPlaceholders, describePlaceholder } from './aiPresetPrompts';
import '../../styles/productAiAssistant.css';

const PERMISSION_KEY = 'product.ai_assistant';

const FIELD_LABELS = {
  website_price: 'Website price',
  dine_in_price: 'Dine-in price',
  takeaway_price: 'Takeaway price',
  status: 'Status',
  name: 'Name',
  short_name: 'Short name',
  category: 'Category',
  subcategory: 'Sub-category',
  force_customization_popup: 'Customisation pop-up',
  is_chilli_enabled: 'Spicy',
  new_product: 'New product',
};

const isMoney = (field) => field === 'website_price' || field === 'dine_in_price' || field === 'takeaway_price';

const formatValue = (value, field) => {
  if (value === null || value === undefined || value === '') return '—';
  if (isMoney(field) && typeof value === 'number') return `$${value.toFixed(2)}`;
  return String(value);
};

/**
 * "AI Assistant" tab on Product Management.
 *
 * Three states, in order, and the operator always sees all three:
 *   1. compose  — pick a preset (which pastes editable text) or write your own
 *   2. review   — the itemised before/after list the backend expanded from the model's proposal
 *   3. apply    — a manager PIN, validated server-side in the same request that writes
 *
 * Nothing is written between 1 and 3. The panel never decides for itself whether a change is
 * allowed: `blocked_reasons` comes back from the server's guardrails and, when present, there is no
 * Apply button to press at all. Keeping that decision server-side matters because the same endpoint
 * is reachable without this UI.
 */
const ProductAiAssistant = ({ onApplied }) => {
  const [unlocked, setUnlocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [selectedPreset, setSelectedPreset] = useState('');
  // The preset's untouched text, plus one value per bracketed slot. The prompt sent to the server is
  // derived from these two until the operator types in the box themselves, at which point the
  // derivation stops so their edit is never overwritten (see handlePromptChange).
  const [presetTemplate, setPresetTemplate] = useState('');
  const [slotValues, setSlotValues] = useState({});
  const [promptEdited, setPromptEdited] = useState(false);
  const [pickLists, setPickLists] = useState({ categories: [], subcategories: [], products: [], sizes: [] });
  const firstSlotRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [isPinOpen, setIsPinOpen] = useState(false);
  const [pinAttempt, setPinAttempt] = useState(0);
  const promptRef = useRef(null);
  const { requirePermission, permissionModalProps } = useRequirePermission();

  const groups = useMemo(() => presetsByGroup(), []);

  // Gate on entry as well as on apply. The apply-time check is the one that actually protects the
  // data (it runs server-side), but asking here too means an unauthorised person doesn't get to
  // compose a plan and hand a half-finished screen to a manager for a rubber-stamp.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setUnlocking(true);
      const { authorized } = await requirePermission(PERMISSION_KEY);
      if (!cancelled) {
        setUnlocked(authorized);
        setUnlocking(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Names for the slot pickers. Fetched here rather than passed down from ProductManagementPage so
  // this panel still doesn't wait on that page's product/category load - the same reason the AI tabs
  // render ahead of its loading gate. Failure is non-fatal: a slot with no list falls back to a
  // plain text input, which is exactly the behaviour before pickers existed.
  useEffect(() => {
    if (!unlocked) return;
    let cancelled = false;
    (async () => {
      const wanted = [
        ['categories', apiUrls.PRODUCT_MANAGEMENT_CATEGORIES],
        ['subcategories', apiUrls.PRODUCT_MANAGEMENT_SUBCATEGORIES],
        ['products', apiUrls.PRODUCT_MANAGEMENT_PRODUCTS],
        ['sizes', apiUrls.PRODUCT_MANAGEMENT_SIZES],
      ];
      const settled = await Promise.all(wanted.map(async ([key, url]) => {
        try {
          const res = await callAPI(url, {}, 'GET', null, {}, {}, false);
          const rows = res?.data?.data || res?.data || [];
          const names = (Array.isArray(rows) ? rows : [])
            .map((row) => row?.name || row?.title || row?.categoryname || row?.itemname)
            .filter((name) => typeof name === 'string' && name.trim() !== '');
          return [key, [...new Set(names)].sort((a, b) => a.localeCompare(b))];
        } catch {
          return [key, []];
        }
      }));
      if (!cancelled) setPickLists(Object.fromEntries(settled));
    })();
    return () => { cancelled = true; };
  }, [unlocked]);

  /** Substitutes whatever slots have been filled; unfilled ones stay bracketed so they stay visible. */
  const buildPrompt = (template, values) =>
    extractPlaceholders(template).reduce(
      (text, token) => (values[token] ? text.split(token).join(values[token]) : text),
      template
    );

  const handlePresetChange = (e) => {
    const key = e.target.value;
    setSelectedPreset(key);
    const preset = findPreset(key);
    if (!preset) return;

    setPresetTemplate(preset.prompt);
    setSlotValues({});
    setPromptEdited(false);
    setPrompt(preset.prompt);

    // Focus the first slot control when there is one to fill, otherwise the box itself. Presets with
    // no slots are effectively one-click, so sending them shouldn't need a detour through the pickers.
    const hasSlots = extractPlaceholders(preset.prompt).length > 0;
    setTimeout(() => {
      if (hasSlots) firstSlotRef.current?.focus();
      else promptRef.current?.focus();
    }, 0);
  };

  const handleSlotChange = (token, value) => {
    const next = { ...slotValues, [token]: value };
    setSlotValues(next);
    // Typing in the box detaches it from the template, so only re-derive while it is still attached.
    if (!promptEdited) setPrompt(buildPrompt(presetTemplate, next));
  };

  const handlePromptChange = (e) => {
    setPrompt(e.target.value);
    if (presetTemplate) setPromptEdited(true);
  };

  const slots = useMemo(
    () => (promptEdited ? [] : extractPlaceholders(presetTemplate)),
    [presetTemplate, promptEdited]
  );

  const handleSend = async () => {
    const text = prompt.trim();
    if (!text) return;

    if (text.includes('[') && text.includes(']')) {
      toast.error('Replace the [bracketed] parts of the prompt with real names or amounts first.');
      promptRef.current?.focus();
      return;
    }

    setSending(true);
    setResult(null);
    try {
      const res = await callAPI(apiUrls.PRODUCT_AI_CHAT, {}, 'POST', {
        prompt: text,
        preset_key: selectedPreset || null,
        created_by_employee_id: Number(localStorage.getItem('active_staff_id')) || null,
      }, {});
      setResult({ ...(res.data?.data || {}), prompt: text });
    } catch (err) {
      console.error('Product AI chat failed', err);
      toast.error(err?.response?.data?.error || 'The assistant is unavailable right now.');
    } finally {
      setSending(false);
    }
  };

  const handleApplyClick = () => {
    setPinAttempt((n) => n + 1);
    setIsPinOpen(true);
  };

  const handleApplyConfirm = async (pin) => {
    if (!result?.proposal_id) return;
    setApplying(true);
    try {
      const res = await callAPI(apiUrls.PRODUCT_AI_CONFIRM, {}, 'POST', {
        proposal_id: result.proposal_id,
        pin,
      }, {});
      const data = res.data?.data || {};
      toast.success(
        `${data.changes_written || 0} change${data.changes_written === 1 ? '' : 's'} applied`
        + (data.authorised_by ? ` (authorised by ${data.authorised_by})` : '')
      );
      setIsPinOpen(false);
      setResult(null);
      setPrompt('');
      setPresetTemplate('');
      setSlotValues({});
      setPromptEdited(false);
      setSelectedPreset('');
      if (onApplied) onApplied();
    } catch (err) {
      // Wrong PIN, an unauthorised PIN, or a stale plan the server refused to replay. Keep the
      // prompt up for another try but remount it so the digit boxes start empty.
      toast.error(err?.response?.data?.error || 'Could not apply the changes.');
      setPinAttempt((n) => n + 1);
    } finally {
      setApplying(false);
    }
  };

  const handleDiscard = async () => {
    if (result?.proposal_id) {
      try {
        await callAPI(apiUrls.PRODUCT_AI_REJECT, {}, 'POST', { proposal_id: result.proposal_id }, {});
      } catch (err) {
        console.error('Failed to record the rejection', err);
      }
    }
    setResult(null);
  };

  // The permission modal has to stay mounted through every state, including the initial
  // "checking" one — that state is precisely when the PIN prompt needs to be on screen, since
  // requirePermission()'s promise doesn't resolve until someone enters a PIN or cancels.
  if (unlocking || !unlocked) {
    return (
      <div className="printerCard">
        <h5 className="aiPanelTitle mb-1">AI Assistant</h5>
        <p className="mb-0 text-muted">
          {unlocking
            ? 'A manager PIN is required to use the AI assistant.'
            : 'Not authorised. Switch to another tab and back to try again.'}
        </p>
        <ValidatePinModal {...permissionModalProps} />
      </div>
    );
  }

  const blocked = result?.blocked_reasons || [];
  const operations = result?.operations || [];
  const warnings = result?.warnings || [];
  const outOfScope = result?.out_of_scope || null;
  const canApply = result?.has_proposal && blocked.length === 0 && !outOfScope && operations.length > 0;

  return (
    <div className="aiAssistantWrap">
      <div className="printerCard">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
          <div>
            <h5 className="aiPanelTitle mb-1">AI Assistant</h5>
            <p className="aiPanelSubtitle mb-0">
              Describe a menu change in plain English. Nothing is saved until you review the exact
              before/after list and authorise it with a manager PIN.
            </p>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-12 col-lg-5">
            <label className="aiFieldLabel" htmlFor="aiPresetSelect">Start from a common request</label>
            <select
              id="aiPresetSelect"
              className="form-select"
              value={selectedPreset}
              onChange={handlePresetChange}
            >
              <option value="">Choose an action…</option>
              {groups.map((group) => (
                <optgroup key={group.key} label={group.label}>
                  {group.presets.map((preset) => (
                    <option key={preset.key} value={preset.key}>{preset.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            <p className="aiFieldHint">
              {slots.length > 0
                ? 'Fill in the blanks below — the request on the right updates as you go.'
                : 'Picking one fills the box on the right so you can edit it, or type your own request.'}
            </p>

            {slots.length > 0 && (
              <div className="aiSlotFields">
                {slots.map((token, index) => {
                  const field = describePlaceholder(token);
                  const options = field.source ? (pickLists[field.source] || []) : null;
                  const id = `aiSlot-${index}`;
                  const value = slotValues[token] || '';
                  const attach = index === 0 ? firstSlotRef : null;

                  return (
                    <div className="aiSlotField" key={token}>
                      <label className="aiSlotLabel" htmlFor={id}>{field.label}</label>

                      {/* A source with an empty list means the fetch failed or the restaurant has
                          none of that thing - fall back to typing rather than showing a dead select. */}
                      {options && options.length > 0 ? (
                        <select
                          id={id}
                          ref={attach}
                          className="form-select form-select-sm"
                          value={value}
                          onChange={(e) => handleSlotChange(token, e.target.value)}
                          disabled={sending}
                        >
                          <option value="">Choose {field.label.toLowerCase()}…</option>
                          {options.map((name) => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                      ) : (
                        <div className={field.prefix ? 'aiSlotPrefixWrap' : undefined}>
                          {field.prefix && <span className="aiSlotPrefix">{field.prefix}</span>}
                          <input
                            id={id}
                            ref={attach}
                            type={field.type || 'text'}
                            step={field.step}
                            min={field.min}
                            className="form-control form-control-sm"
                            value={value}
                            placeholder={options ? `Type a ${field.label.toLowerCase()}` : field.label}
                            onChange={(e) => handleSlotChange(token, e.target.value)}
                            disabled={sending}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="col-12 col-lg-7">
            <label className="aiFieldLabel" htmlFor="aiPromptBox">Your request</label>
            <textarea
              id="aiPromptBox"
              ref={promptRef}
              className="form-control aiPromptBox"
              rows={4}
              value={prompt}
              placeholder={'e.g. "Increase all pizza prices by 8% and round to the nearest 50c", or ask a question like "which products have no price set?"'}
              onChange={handlePromptChange}
              disabled={sending}
            />
            <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
              <span className="aiFieldHint mb-0">
                Covers this restaurant's products, categories, pricing and availability only — it
                can add, rename, duplicate, re-price, re-categorise and enable/disable, and cannot
                delete anything.
              </span>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSend}
                disabled={sending || !prompt.trim()}
              >
                {sending ? 'Thinking…' : 'Ask the assistant'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {result && (
        <div className="printerCard mt-3">
          <div className="aiReviewHeader">
            <h6 className="mb-1">What the assistant proposes</h6>
            <span className="aiReviewPrompt">“{result.prompt}”</span>
          </div>

          {/* An out-of-scope request isn't an error and shouldn't look like one — it's the
              assistant declining. Rendered as a neutral notice, above the summary, so the operator
              reads "wrong tool for this" rather than "something broke". */}
          {outOfScope && (
            <div className="aiOutOfScope">
              <strong>
                {outOfScope.category === 'other_orderart_area'
                  ? 'Handled on another screen'
                  : 'Outside what this assistant does'}
              </strong>
              <div className="aiOutOfScopeBody">{result.summary}</div>
              {outOfScope.topic && outOfScope.topic !== 'unspecified' && (
                <div className="aiFieldHint mb-0">You asked about: {outOfScope.topic}</div>
              )}
            </div>
          )}

          {!outOfScope && result.summary && <p className="aiSummary">{result.summary}</p>}

          {blocked.length > 0 && (
            <div className="aiBlocked">
              <strong>This request was not allowed:</strong>
              <ul className="mb-0">
                {blocked.map((reason, i) => <li key={i}>{reason}</li>)}
              </ul>
            </div>
          )}

          {warnings.map((warning, i) => (
            <div key={i} className="aiWarning">{warning}</div>
          ))}

          {operations.length > 0 && (
            <>
              <div className="aiAffectedCount">
                {result.affected_count} value{result.affected_count === 1 ? '' : 's'} would change
                across {operations.length} action{operations.length === 1 ? '' : 's'}.
              </div>

              {operations.map((op, oi) => (
                <div key={oi} className="aiOperation">
                  <div className="aiOperationTitle">{op.description || op.type}</div>
                  {op.changes.length === 0 ? (
                    <p className="aiFieldHint mb-0">Nothing to change — already in this state.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table aiChangeTable mb-0">
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>What changes</th>
                            <th>From</th>
                            <th>To</th>
                          </tr>
                        </thead>
                        <tbody>
                          {op.changes.map((change, ci) => (
                            <tr key={ci}>
                              <td>
                                {change.product_name}
                                {change.size_name && <span className="aiSizeTag">{change.size_name}</span>}
                              </td>
                              <td>{FIELD_LABELS[change.field] || change.field}</td>
                              <td className="aiBefore">{formatValue(change.before, change.field)}</td>
                              <td className="aiAfter">{formatValue(change.after, change.field)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {operations.length === 0 && blocked.length === 0 && !outOfScope && (
            <p className="aiFieldHint mb-0">
              No changes were proposed — the answer above is informational only.
            </p>
          )}

          <div className="d-flex gap-2 mt-3 flex-wrap">
            {canApply && (
              <button type="button" className="btn btn-success" onClick={handleApplyClick} disabled={applying}>
                {applying ? 'Applying…' : 'Apply with manager PIN'}
              </button>
            )}
            <button type="button" className="btn btn-secondary" onClick={handleDiscard} disabled={applying}>
              {canApply ? 'Discard' : 'Close'}
            </button>
          </div>
        </div>
      )}

      {isPinOpen && (
        <ValidatePinModal
          key={pinAttempt}
          isOpen={true}
          onCancel={() => setIsPinOpen(false)}
          onConfirm={handleApplyConfirm}
          title="Authorise Menu Changes"
          subtitle={`Manager PIN required to apply ${result?.affected_count || 0} change${result?.affected_count === 1 ? '' : 's'}`}
        />
      )}

      <ValidatePinModal {...permissionModalProps} />
    </div>
  );
};

export default ProductAiAssistant;
